import { useQuery } from '@tanstack/react-query'

import { useSettings } from '@loadsmart/react-frontend-settings'

import { useSession } from 'auth/ui/hooks/useSession'
import { isForbiddenError } from 'core/infra/http/errors'
import { useSettingsSettled } from 'core/ui/hooks/useSettingsSettled'
import type { OpenDockOrgReport } from 'managed-analytics/domain/OpenDockOrgReport'
import { getOpenDockReports } from 'managed-analytics/use-cases/getOpenDockReports'

import {
  resolveOpenDockPdfReportsAccess,
  type OpenDockReportsAccessState,
} from './resolveOpenDockPdfReportsAccess'

export const OPENDOCK_PDF_REPORTS_FLAG = 'flags.ENABLE_OPENDOCK_PDF_REPORTS'

type OpenDockReportsAccess = {
  isAllowed: boolean
  reports: OpenDockOrgReport[]
}

export function useOpenDockPdfReportsAccess() {
  const session = useSession()
  const settingsSettled = useSettingsSettled()
  const { isLoading: isSettingsLoading, values } = useSettings([OPENDOCK_PDF_REPORTS_FLAG])
  const isFlagLoading = !settingsSettled || isSettingsLoading
  const isFlagEnabled =
    values?.[0] === null || values?.[0] === undefined ? null : Boolean(values[0])
  const isOpenDock = session?.user.issuer === 'opendock'
  const orgId = session?.user.org_id ?? session?.user.client ?? null

  const { shouldFetch } = resolveOpenDockPdfReportsAccess({
    hasSession: Boolean(session),
    isOpenDock,
    isFlagLoading,
    isFlagEnabled,
    accessState: 'pending',
  })

  const query = useQuery<OpenDockReportsAccess>({
    queryKey: ['opendockReports', orgId],
    queryFn: async ({ signal }) => {
      try {
        return { isAllowed: true, reports: await getOpenDockReports({ signal }) }
      } catch (error) {
        // A 403 is an answer, not a failure, so it is resolved as data. That
        // keeps the verdict in the cache: query-core clears `error` back to
        // null every time it refetches a query that has no data, and this query
        // refetches on window focus, which used to flash the nav item on for an
        // org without access until the next 403 landed.
        if (isForbiddenError(error)) return { isAllowed: false, reports: [] }
        throw error
      }
    },
    enabled: shouldFetch,
    retry: false,
    // Every page renders its own AppLayout, so a route change remounts the nav
    // and refetches on mount; window focus refetches too. Neither can change
    // the access verdict, and a report lands every few days at most, so keeping
    // the answer fresh for a couple of minutes drops the redundant calls.
    staleTime: 2 * 60 * 1000,
  })

  // Once anything has settled the answer sticks. `data` survives refetches, and
  // a hard failure counts as "no access" instead of dropping back to pending,
  // so the menu never rearranges itself after it has rendered.
  const accessState: OpenDockReportsAccessState = query.data
    ? query.data.isAllowed
      ? 'granted'
      : 'denied'
    : query.isFetched
      ? 'denied'
      : 'pending'

  const { isLoading, isNavVisible } = resolveOpenDockPdfReportsAccess({
    hasSession: Boolean(session),
    isOpenDock,
    isFlagLoading,
    isFlagEnabled,
    accessState,
  })

  return {
    isLoading,
    isNavVisible,
    reports: query.data?.reports ?? [],
    isError: accessState === 'denied' && !query.data,
    isForbidden: query.data?.isAllowed === false,
  }
}
